import { forwardRef } from 'react';

const clientTypes = [
  { icon: 'ri-government-line', label: 'Heads of State', sub: 'Sovereign leaders preparing for the global stage' },
  { icon: 'ri-user-star-line', label: 'Cabinet Ministers', sub: 'Ministers commanding international forums' },
  { icon: 'ri-building-2-line', label: 'Members of Parliament', sub: 'MPs engaging G7 & EAC counterparts' },
  { icon: 'ri-briefcase-4-line', label: 'Business Elites', sub: 'Executives operating at the apex of power' },
  { icon: 'ri-global-line', label: 'NGO Leadership', sub: 'Institutions seeking international credibility' },
  { icon: 'ri-shield-star-line', label: 'State Institutions', sub: 'Agencies aligning with global standards' },
];

const WhoWeServe = forwardRef<HTMLElement>((props, ref) => {
  return (
    <section
      ref={ref}
      className="bg-[#0D0C0A] py-[200px] md:py-[200px] py-[100px] opacity-0 transition-all duration-[800ms]"
      id="who-we-serve"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-[80px]">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-[100px] md:mb-[100px] mb-[60px] gap-[32px] md:gap-0">
          <div>
            <p className="text-[#C9A96E] text-[10px] uppercase tracking-[0.45em] font-sans mb-[28px] md:mb-[28px] mb-[20px]">
              Who We Serve
            </p>
            <h2
              className="font-serif text-[#F8F6F3] leading-[0.95] tracking-[-0.01em]"
              style={{ fontSize: 'clamp(36px, 5vw, 72px)', fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              Africa&apos;s Most<br />Consequential Leaders
            </h2>
          </div>
          <p className="text-[#F8F6F3]/45 text-[14px] md:text-[15px] font-sans leading-[1.7] max-w-[400px] md:text-right">
            We serve those for whom the stakes are national,<br />
            the audience is global, and failure is not an option.
          </p>
        </div>

        {/* Client type grid */}
        <div className="grid grid-cols-2 md:grid-cols-6 border border-[#3A3D42] mb-[160px] md:mb-[160px] mb-[80px]">
          {clientTypes.map((c, i) => (
            <div
              key={i}
              className="flex flex-col items-center text-center px-[20px] md:px-[28px] py-[40px] md:py-[52px] border-r border-b md:border-b-0 border-[#3A3D42] last:border-r-0 md:[&:nth-child(6)]:border-r-0 [&:nth-child(2n)]:border-r-0 md:[&:nth-child(2n)]:border-r [&:nth-child(5)]:border-b-0 [&:nth-child(6)]:border-b-0 hover:bg-[#1A1917] transition-colors duration-400 cursor-default"
            >
              <div className="w-[40px] h-[40px] flex items-center justify-center mb-[24px]">
                <i className={`${c.icon} text-[28px] text-[#C9A96E]`} />
              </div>
              <p
                className="text-[#F8F6F3] font-serif mb-[12px]"
                style={{ fontSize: 'clamp(14px, 2.5vw, 16px)', fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '0.02em' }}
              >
                {c.label}
              </p>
              <p className="text-[#F8F6F3]/35 font-sans" style={{ fontSize: '11px', letterSpacing: '0.02em', lineHeight: 1.6 }}>
                {c.sub}
              </p>
            </div>
          ))}
        </div>

        {/* ── THE ANTEROOM STANDARD ── */}
        <div className="mb-[80px] md:mb-[80px] mb-[60px]">
          <p className="text-[#C9A96E] text-[10px] uppercase tracking-[0.45em] font-sans mb-[28px] md:mb-[28px] mb-[20px]">
            The Anteroom Standard
          </p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-[24px] md:gap-0">
            <h2
              className="font-serif text-[#F8F6F3] leading-[0.95] tracking-[-0.01em]"
              style={{ fontSize: 'clamp(32px, 4.5vw, 66px)', fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              Three Principles.<br />
              <em style={{ fontStyle: 'italic', color: 'rgba(248,246,243,0.55)' }}>No Exceptions.</em>
            </h2>
            <p className="text-[#F8F6F3]/40 text-[13px] md:text-[14px] font-sans leading-[1.8] max-w-[380px] md:text-right">
              The institutions that endure are built on principles that do not bend.<br />
              This is the foundation of everything we do.
            </p>
          </div>
        </div>

        {/* Three standards — tall portrait cards */}
        <div className="flex flex-col md:grid md:grid-cols-3 gap-[2px] mb-[160px] md:mb-[160px] mb-[80px]">
          {[
            {
              number: 'I',
              title: 'Absolute Discretion',
              body: 'Every engagement is sealed in confidence. The leaders we prepare trust us with their most sensitive moments of transition. Nothing leaves the anteroom.',
              image: 'https://public.readdy.ai/ai/img_res/e2fb2d42fcd15030b3d7947c3b06d763.jpg',
            },
            {
              number: 'II',
              title: 'Sovereign Calibre',
              body: 'We do not work with everyone. Our mandate is reserved for those who carry the weight of nations — leaders for whom a single room, a single sentence, a single moment can alter the course of history.',
              image: 'https://readdy.ai/api/search-image?query=Empty%20parliament%20chamber%20with%20tiered%20curved%20wooden%20seats%2C%20dramatic%20overhead%20spotlights%20casting%20warm%20golden%20pools%20of%20light%2C%20deep%20shadows%20in%20upper%20gallery%2C%20gold%20accents%20on%20central%20podium%20and%20speaker%5Cs%20chair%2C%20no%20people%2C%20monumental%20neoclassical%20architecture%2C%20cinematic%208K%20composition%2C%20dark%20and%20authoritative%20atmosphere%2C%20ultra%20sharp%20detail%2C%20luxury%20institutional%20interior%2C%20warm%20gold%20and%20dark%20wood%20tones%2C%20photorealistic%2C%20Westminster%20or%20African%20Union%20parliament%20style&width=700&height=1000&seq=standard-room-hd-2-v6&orientation=portrait',
            },
            {
              number: 'III',
              title: 'Irreversible Transformation',
              body: 'Anteroom does not offer training. We deliver transformation. When a leader completes our process, they do not return to who they were. They step permanently into a higher register of authority.',
              image: 'https://readdy.ai/api/search-image?query=Stately%20private%20executive%20library%20with%20floor%20to%20ceiling%20dark%20mahogany%20bookshelves%20filled%20with%20leather-bound%20volumes%2C%20single%20large%20antique%20desk%20with%20brass%20lamp%20casting%20warm%20amber%20glow%2C%20tall%20window%20with%20city%20skyline%20at%20dusk%2C%20deep%20Persian%20rug%2C%20no%20people%2C%20warm%20amber%20light%2C%20dark%20wood%20paneling%2C%20cinematic%208K%20composition%2C%20sovereign%20luxury%20atmosphere%2C%20ultra%20sharp%20detail%2C%20deep%20warm%20tones%2C%20photorealistic%2C%20private%20club%20or%20embassy%20library%20aesthetic&width=700&height=1000&seq=standard-room-hd-3-v6&orientation=portrait',
            },
          ].map((s, i) => (
            <div key={i} className="relative overflow-hidden group h-[450px] md:h-[700px]">
              <img
                src={s.image}
                alt={s.title}
                className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                style={{ filter: 'saturate(0) contrast(1.1)' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/98 via-black/60 to-black/15" />

              <div className="absolute inset-0 flex flex-col justify-between p-[36px] md:p-[52px]">
                {/* Roman numeral top */}
                <div className="flex items-center gap-4">
                  <span
                    className="text-[#C9A96E]/30 font-serif"
                    style={{ fontSize: 'clamp(56px, 10vw, 72px)', lineHeight: 1, fontFamily: '"Playfair Display", Georgia, serif' }}
                  >
                    {s.number}
                  </span>
                  <div className="w-[1px] h-[48px] bg-[#C9A96E]/20" />
                </div>

                {/* Bottom content */}
                <div>
                  <div className="w-[32px] h-[1px] bg-[#C9A96E] mb-[28px] md:mb-[28px] mb-[20px]" />
                  <h3
                    className="font-serif text-[#F8F6F3] mb-[20px] leading-[1.1]"
                    style={{ fontSize: 'clamp(20px, 3.5vw, 26px)', fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '0.02em' }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className="text-[#F8F6F3]/60 font-sans leading-[1.8]"
                    style={{ fontSize: 'clamp(13px, 2vw, 14px)', letterSpacing: '0.015em' }}
                  >
                    {s.body}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── MANIFESTO STRIP ── */}
        <div
          className="relative overflow-hidden px-[40px] md:px-[100px] py-[60px] md:py-[100px]"
          style={{ backgroundColor: '#111009', border: '1px solid #2A2825' }}
        >
          {/* Background watermark */}
          <div
            className="absolute right-[-40px] top-1/2 -translate-y-1/2 font-serif text-white/[0.02] select-none pointer-events-none"
            style={{
              fontSize: 'clamp(100px, 14vw, 180px)',
              fontFamily: '"Playfair Display", Georgia, serif',
              letterSpacing: '0.1em',
              whiteSpace: 'nowrap',
            }}
          >
            ANTEROOM
          </div>

          <div className="relative z-10 flex flex-col md:grid md:grid-cols-2 gap-[60px] md:gap-[80px] items-center">
            <div>
              <div className="flex items-center gap-4 mb-[40px] md:mb-[40px] mb-[28px]">
                <div className="w-[32px] h-[1px] bg-[#C9A96E]" />
                <span className="text-[#C9A96E] font-sans uppercase" style={{ fontSize: '9px', letterSpacing: '0.4em' }}>
                  Our Mandate
                </span>
              </div>
              <p
                className="font-serif text-[#F8F6F3] leading-[1.2] mb-[40px] md:mb-[40px] mb-[28px]"
                style={{
                  fontSize: 'clamp(22px, 3vw, 44px)',
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontStyle: 'italic',
                }}
              >
                &ldquo;We do not prepare leaders to attend the room. We prepare them to own it.&rdquo;
              </p>
              <div className="flex items-center gap-4">
                <div className="w-[24px] h-[1px] bg-[#C9A96E]/50" />
                <span className="text-[#C9A96E]/60 font-sans uppercase" style={{ fontSize: '8px', letterSpacing: '0.35em' }}>
                  Anteroom Sovereign Advisory
                </span>
              </div>
            </div>

            {/* Right — institutional pillars */}
            <div className="grid grid-cols-2 gap-[1px] border border-[#2A2825] w-full">
              {[
                { value: 'G7', label: 'Summit Standard' },
                { value: 'AU', label: 'Continental Mandate' },
                { value: 'UN', label: 'Global Stage Ready' },
                { value: 'WEF', label: 'Davos Calibre' },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center justify-center py-[36px] md:py-[48px] px-[24px] md:px-[32px] text-center"
                  style={{ backgroundColor: '#0D0C0A', borderRight: i % 2 === 0 ? '1px solid #2A2825' : 'none', borderBottom: i < 2 ? '1px solid #2A2825' : 'none' }}
                >
                  <p
                    className="font-serif text-[#C9A96E] mb-[10px]"
                    style={{ fontSize: 'clamp(28px, 6vw, 36px)', fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '0.04em' }}
                  >
                    {item.value}
                  </p>
                  <p className="text-[#F8F6F3]/35 font-sans uppercase" style={{ fontSize: '9px', letterSpacing: '0.22em' }}>
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
});

WhoWeServe.displayName = 'WhoWeServe';

export default WhoWeServe;
